import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Download, Image as ImageIcon, Check } from 'lucide-react';

/**
 * DownloadSizeDialog — Turbo 360 "Cinema" dark card.
 * Radio-style size options (selected = red ring + red tint), full-width red CTA.
 */
const OPTIONS = [
  { id: 'full', label: 'Full resolution', meta: 'Original size · highest quality · JPG', hint: 'Perfect for printing and professional use', icon: ImageIcon },
  { id: 'social', label: 'Social size', meta: 'Max 2048px wide · smaller file · JPG', hint: 'Optimised for web and social media', icon: Download },
];

const DownloadSizeDialog = ({ isOpen, onClose, onSelectSize, photoCount = 1, isLoading = false }) => {
  const [selected, setSelected] = useState('full');
  useEffect(() => { if (isOpen) setSelected('full'); }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={isLoading ? undefined : onClose}>
      <DialogContent
        className="sm:max-w-md font-tiq-body border-0 text-white"
        style={{ background: '#141416', borderRadius: 14, border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <DialogHeader>
          <DialogTitle className="font-tiq-display uppercase" style={{ color: '#fff', fontWeight: 800, letterSpacing: '-0.015em' }}>
            {isLoading ? 'Preparing download…' : 'Choose download size'}
          </DialogTitle>
          <DialogDescription style={{ color: 'rgba(255,255,255,0.55)' }}>
            {isLoading
              ? 'Please wait while we prepare your download…'
              : `Select the image size for your download${photoCount > 1 ? ` · ${photoCount} photos` : ''}`}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-12 h-12 rounded-full animate-spin" style={{ border: '3px solid rgba(191,33,47,0.25)', borderTopColor: '#BF212F' }}></div>
            <p className="mt-4 font-tiq-mono tiq-kicker" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Preparing your download…</p>
          </div>
        ) : (
          <>
            <div className="space-y-3 mt-2">
              {OPTIONS.map((opt) => {
                const on = selected === opt.id;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setSelected(opt.id)}
                    className="w-full text-left flex items-start gap-3 transition-all"
                    style={{
                      padding: 14,
                      borderRadius: 12,
                      background: on ? 'rgba(191,33,47,0.12)' : 'rgba(255,255,255,0.04)',
                      border: on ? '2px solid #BF212F' : '2px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <div className="flex-shrink-0 flex items-center justify-center" style={{ width: 38, height: 38, borderRadius: 10, background: on ? '#BF212F' : 'rgba(255,255,255,0.07)' }}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-tiq-heading font-semibold text-white">{opt.label}</h3>
                      <p className="font-tiq-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 3, letterSpacing: '0.02em' }}>{opt.meta}</p>
                      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>{opt.hint}</p>
                    </div>
                    {on && (
                      <div className="flex-shrink-0 flex items-center justify-center" style={{ width: 22, height: 22, borderRadius: '50%', background: '#BF212F' }}>
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => onSelectSize(selected)}
              className="font-tiq-heading w-full inline-flex items-center justify-center gap-2 text-white mt-2"
              style={{ background: '#BF212F', borderRadius: 10, padding: '13px 18px', fontWeight: 600, cursor: 'pointer' }}
            >
              <Download className="w-4 h-4" /> Download
            </button>
            <button
              onClick={onClose}
              className="font-tiq-heading w-full text-center"
              style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: 600, padding: '6px', cursor: 'pointer', background: 'transparent', border: 'none' }}
            >
              Cancel
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DownloadSizeDialog;
